import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Modal, Form, Row, Col, Input, Space, Switch, notification } from 'antd';
import { useUserSettingsLogic } from '../useUserSettingsLogic';
import { firstName, lastName, emailRegex } from '../../validation/regex';
import { useI18nLocalization } from '../../i18nLocalization/useI18nLocalization';
import './style.less';

const CreateUserModal = ({ isVisible, onSaveCallback, onCloseCallback }) => {
	const roles = useSelector((state) => state.company?.roles ?? []);

	const userSettingsLogic = useUserSettingsLogic();

	const [form] = Form.useForm();
	const [isSaving, setIsSaving] = useState(false);
	const [areFormErrors, setAreFormErrors] = useState(true);

	const { t } = useI18nLocalization();

	//-- Need to manually keep track of the roles assigned to the user b/c the control for this in the form is custom
	const [userRoleIds, setUserRoleIds] = useState([]);

	//-- Put the form values in to the structure expected by the API
	const getNewUser = (formValues) => {
		const newUserObj = {
			firstName: formValues.firstName,
			lastName: formValues.lastName,
			emailAddress: formValues.emailAddress,
			roleIds: userRoleIds,
			userEnabled: formValues.isActive
		};
		return newUserObj;
	};

	const handleOnSave = async (formValues) => {
		setIsSaving(true);
		const newUser = getNewUser(formValues);
		const successful = await userSettingsLogic.createUser(newUser);
		if (successful) {
			notification['success']({
				message: t(`User created!`)
			});

			clearForm();
			onSaveCallback();
		} else {
			notification['error']({
				message: t(
					`We encountered an error creating the user for this profile. Please try again. If it persists, please submit an EntDev Helpdesk ticket.`
				),
				duration: 10
			});
		}

		setIsSaving(false);
	};

	const handleOnCancel = () => {
		setIsSaving(false);
		clearForm();
		onCloseCallback();
	};

	const clearForm = () => {
		form.resetFields();
		setUserRoleIds([]);
	};

	return (
		<>
			<Modal
				title={t('Create User for this Profile')}
				visible={isVisible}
				okText={t('Save')}
				onOk={() => {
					//-- Will trigger the form's onFinish event
					form.submit();
				}}
				okButtonProps={{ disabled: areFormErrors }}
				confirmLoading={isSaving}
				onCancel={handleOnCancel}
				cancelText={t('Cancel')}
			>
				<Form
					form={form}
					onFieldsChange={() => {
						const areErrors = form.getFieldsError().some((fe) => fe.errors.length > 0);
						setAreFormErrors(areErrors);
					}}
					layout='vertical'
					requiredMark
					scrollToFirstError
					onFinish={handleOnSave}
				>
					<Row gutter={[16, 0]}>
						<Col span={24} lg={{ span: 12 }}>
							<Form.Item
								label={t('First Name')}
								name='firstName'
								required
								rules={[
									{
										validator: (field, value) => {
											if (!value) {
												return Promise.reject(new Error(t('First name is required')));
											}

											if (!value.match(firstName)) {
												return Promise.reject(new Error(`1-50 ${t('alpha chars (inclusive)')}`));
											}

											return Promise.resolve();
										}
									}
								]}
							>
								<Input placeholder={t('first name')} />
							</Form.Item>
						</Col>
						<Col span={24} lg={{ span: 12 }}>
							<Form.Item
								label={t('Last Name')}
								name='lastName'
								required
								rules={[
									{
										validator: (field, value) => {
											if (!value) {
												return Promise.reject(new Error(t('Last name is required')));
											}

											if (!value.match(lastName)) {
												return Promise.reject(new Error(`1-50 ${t('alpha chars (inclusive)')}`));
											}

											return Promise.resolve();
										}
									}
								]}
							>
								<Input placeholder={t('last name')} />
							</Form.Item>
						</Col>
					</Row>
					<Form.Item
						label={t('Email Address')}
						name='emailAddress'
						required
						rules={[
							{
								validator: (field, value) => {
									if (!value.match(emailRegex)) {
										return Promise.reject(new Error(t('Email address is required')));
									}

									return Promise.resolve();
								}
							}
						]}
					>
						<Input placeholder={t('email address')} />
					</Form.Item>
					<Form.Item
						name='roles'
						label={t('Roles')}
						required
						rules={[
							{
								validator: () => {
									if (userRoleIds.length === 0) {
										return Promise.reject(new Error(t('A user must have at least one role')));
									}

									return Promise.resolve();
								}
							}
						]}
					>
						<RoleList
							roles={roles}
							userRoleIds={userRoleIds}
							setUserRoleIds={setUserRoleIds}
							onChange={() => {
								return null;
							}}
						/>
					</Form.Item>
					<Form.Item name='isActive' label={t('Active')} required valuePropName='checked'>
						<Switch />
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};

CreateUserModal.propTypes = {
	isVisible: PropTypes.bool.isRequired,
	onSaveCallback: PropTypes.func.isRequired,
	onCloseCallback: PropTypes.func.isRequired
};

//-- Need to do this beceause the validator requires an onChange event be on the top-most child element of Form.Input
//--	It also requires a value prop, but this control will have multiple values, so we'll manually keep track of it's values
//--	Setting up RoleList this way allows us to use the nice rules validator in Form.Input
const RoleList = ({ roles, userRoleIds, setUserRoleIds, onChange }) => {
	const sortedRoles = _.orderBy(roles, ['name'], ['asc']);
	const { t } = useI18nLocalization();
	return (
		<Space direction='vertical'>
			{_.map(sortedRoles, (sr) => {
				return (
					<Space key={sr.id} direction='horizontal'>
						<Switch
							checked={userRoleIds.includes(sr.id)}
							onChange={(newValue) => {
								//-- Adding the role
								if (newValue) {
									setUserRoleIds(userRoleIds.concat([sr.id]));
									onChange();
								}
								//-- Removing the role
								else {
									setUserRoleIds(userRoleIds.filter((urid) => urid !== sr.id));
									onChange();
								}
							}}
						/>
						<span>
							{sr.name} <i>({t(sr.description)})</i>
						</span>
					</Space>
				);
			})}
		</Space>
	);
};

RoleList.propTypes = {
	roles: PropTypes.array.isRequired,
	userRoleIds: PropTypes.array.isRequired,
	setUserRoleIds: PropTypes.func.isRequired,
	onChange: PropTypes.func.isRequired
};

export default CreateUserModal;
